import React, { useCallback, useRef, useState } from 'react';
import { useWikiV2Client } from '../../../../hooks/useWikiV2';
import { useWikiQuery } from '../../shared/useWikiQueries';
import { wikiUi } from '../../shared/wikiUi';
import { WikiError, WikiLoading } from '../../shared/WikiStates';
import TopicTable from './TopicTable';
import TopicDrilldown from './TopicDrilldown';
import {
    BRAIN_OPS_TOASTS,
    brainErrorToast,
    brainOpsKeys,
    brainOpsPaths,
    useBrainAccess,
} from '../review/brainOpsShared';
import '../review/brainOps.css';
import './brainInsights.css';

/**
 * AI Brain › Emotion insights (E2 §2). KPI cards (always visible), then either
 * the topic table ("How people feel, by topic") or — for people with
 * Investigate conversations — one topic's drill-down, which REPLACES the table.
 *
 * Contract (contracts/BE-brain.md §5): GET /wiki/brain/insights (brain_view);
 * GET …/topics/{id}/conversations and POST …/reveal (brain_investigate). Both
 * investigate calls write the audit row server-side BEFORE returning data, so
 * they are fetched imperatively on the click (never cached, never refetched
 * by an effect or a window focus) — one open = one audit row. "Hide names" is
 * client-only; closing the topic re-hides names.
 *
 * Editors: rows stay clickable and raise the prototype denial toast.
 */
export default function BrainInsightsTab(props) {
    const access = useBrainAccess(props);
    const client = useWikiV2Client();
    const query = useWikiQuery(brainOpsKeys.insights(), brainOpsPaths.insights);
    const [drill, setDrill] = useState(null);
    const requestSeq = useRef(0);

    const data = query.data || {};
    const canInvestigate = access.canInvestigate && data.can_investigate !== false;

    const openTopic = useCallback(async (topic) => {
        if (!topic) return;
        if (!canInvestigate) {
            wikiUi.toast(BRAIN_OPS_TOASTS.investigateDenied);
            return;
        }
        requestSeq.current += 1;
        const seq = requestSeq.current;
        setDrill({ topic, status: 'loading', data: null, error: null, revealed: null, revealing: false, convId: null });
        try {
            const res = await client.get(brainOpsPaths.conversations(topic.id));
            if (requestSeq.current !== seq) return;
            setDrill((prev) => (prev ? { ...prev, status: 'ready', data: res || {} } : prev));
        } catch (error) {
            if (requestSeq.current !== seq) return;
            setDrill((prev) => (prev ? { ...prev, status: 'error', error } : prev));
            // MFA step-up, plan gate or audit failure: the server sentence, verbatim.
            wikiUi.toast(brainErrorToast(error));
        }
    }, [canInvestigate, client]);

    const closeTopic = useCallback(() => {
        requestSeq.current += 1;
        setDrill(null);
    }, []);

    const toggleReveal = useCallback(async () => {
        if (!drill || drill.status !== 'ready' || drill.revealing) return;
        if (drill.revealed) {
            setDrill((prev) => (prev ? { ...prev, revealed: null } : prev));
            return;
        }
        const seq = requestSeq.current;
        const topicId = drill.topic.id;
        setDrill((prev) => (prev ? { ...prev, revealing: true } : prev));
        try {
            const res = await client.post(brainOpsPaths.reveal(topicId));
            if (requestSeq.current !== seq) return;
            setDrill((prev) => (prev ? { ...prev, revealing: false, revealed: res || {} } : prev));
            wikiUi.toast(BRAIN_OPS_TOASTS.namesShown);
        } catch (error) {
            if (requestSeq.current !== seq) return;
            setDrill((prev) => (prev ? { ...prev, revealing: false } : prev));
            wikiUi.toast(brainErrorToast(error));
        }
    }, [client, drill]);

    const selectConversation = useCallback((convId) => {
        setDrill((prev) => (prev ? { ...prev, convId } : prev));
    }, []);

    if (query.isLoading) {
        return <div className="wk-card" data-testid="brain-insights-tab"><WikiLoading label="Loading emotion insights…" /></div>;
    }
    if (query.isError) {
        return (
            <div className="wk-card" data-testid="brain-insights-tab">
                <WikiError error={query.error} onRetry={() => query.refetch()} />
            </div>
        );
    }

    const kpis = Array.isArray(data.kpis) ? data.kpis : [];
    const retentionDays = data.emotion?.retention_days;

    return (
        <div className="wk-bri wk-stack" data-testid="brain-insights-tab">
            {kpis.length > 0 && (
                <div className="wk-bri-kpis" data-testid="brain-insights-kpis">
                    {kpis.map((kpi) => (
                        <div key={kpi.id || kpi.label} className="wk-card wk-bri-kpi" data-testid="brain-insights-kpi">
                            <span className="wk-t-label wk-bri-kpi-label">{kpi.label}</span>
                            <div className="wk-bri-kpi-row">
                                <span className="wk-t-stat">{kpi.value}</span>
                                {kpi.delta && <span className="wk-bri-kpi-delta">{kpi.delta}</span>}
                            </div>
                        </div>
                    ))}
                </div>
            )}
            {drill ? (
                <TopicDrilldown
                    drill={drill}
                    retentionDays={retentionDays}
                    onBack={closeTopic}
                    onRetry={() => openTopic(drill.topic)}
                    onToggleReveal={toggleReveal}
                    onSelectConversation={selectConversation}
                />
            ) : (
                <TopicTable
                    topics={Array.isArray(data.topics) ? data.topics : []}
                    legend={data.mood_legend}
                    emotion={data.emotion}
                    windowLabel={data.window?.label}
                    onOpen={openTopic}
                />
            )}
        </div>
    );
}
